/**
 * One browser owner for one marketplace handoff.
 *
 * The page has three independent sources of "the loan sold": the status poll, an Echo
 * broadcast, and the poll's own attempt limit. Previously each of them could act:
 * setInterval let polls overlap, an Echo event reloaded the page from underneath a
 * request in flight, and an unconditional `.finally()` reloaded on attempt 120 even when
 * that very response had just started the handoff. Any two of them firing together could
 * navigate twice or throw away a navigation already under way.
 *
 * This module owns that decision instead, as a small state machine that is pure enough to
 * test: every effect (fetching, submitting, reloading, timing) is injected.
 *
 *   idle -> polling -> claiming -> submitted
 *              |                      ^
 *              +-- (attempt limit) -> stopped (reload)
 *
 * The invariants:
 *  - one request in flight at a time, and the next poll is scheduled only after the
 *    previous one settles, so slow responses stretch the interval instead of stacking;
 *  - `claim()` latches synchronously, so a second caller in the same tick loses;
 *  - once submitted, every late callback - poll, Echo, attempt limit - is ignored;
 *  - a sold result is always processed BEFORE the attempt limit is considered, so a sale
 *    that arrives on the final attempt is handed off rather than reloaded away;
 *  - nothing is claimed implicitly: the sale to consume is whatever the SERVER named in
 *    the poll response, never an id an event happened to carry.
 */

export const HANDOFF_STATE = {
    IDLE: 'idle',
    POLLING: 'polling',
    CLAIMING: 'claiming',
    SUBMITTED: 'submitted',
    STOPPED: 'stopped',
};

/** Reasons surfaced to the host rather than swallowed. */
export const HANDOFF_NOTICE = {
    SOLD_WITHOUT_ACTION: 'sold_without_action',
    STALE_SALE_IGNORED: 'stale_sale_ignored',
    LATE_RESULT_IGNORED: 'late_result_ignored',
    OTHER_LOAN_IGNORED: 'other_loan_ignored',
    POLL_FAILED: 'poll_failed',
    ATTEMPTS_EXHAUSTED: 'attempts_exhausted',
    DECISION_RESOLVED: 'decision_resolved',
};

export const DEFAULT_POLL_INTERVAL_MS = 5000;

// 120 x 5s = 10 minutes, matching the marketplace cross-sell worker timeout: we wait for
// the whole window a sale can take, then let the server render the terminal state.
export const DEFAULT_MAX_ATTEMPTS = 120;

export const DEFAULT_RESOLVED_HANDOFF_DELAY_MS = 1000;

export function createMarketplaceHandoffCoordinator({
    loanId = null,
    checkStatus,
    submitHandoff,
    reload,
    onNotice = () => {},
    pollIntervalMs = DEFAULT_POLL_INTERVAL_MS,
    maxAttempts = DEFAULT_MAX_ATTEMPTS,
    resolvedHandoffDelayMs = DEFAULT_RESOLVED_HANDOFF_DELAY_MS,
    setTimeoutFn = (...args) => setTimeout(...args),
    clearTimeoutFn = (...args) => clearTimeout(...args),
} = {}) {
    let state = HANDOFF_STATE.IDLE;
    let attempts = 0;
    let timer = null;
    let requestInFlight = false;
    // An Echo event that arrives while a poll is already running: re-check as soon as
    // that one settles instead of starting a second request or waiting the interval.
    let recheckWhenIdle = false;
    // Set when the page was rendered with the action already in hand and is waiting out
    // its short display delay before submitting.
    let pendingResolvedResult = null;

    function isFinished() {
        return state === HANDOFF_STATE.CLAIMING
            || state === HANDOFF_STATE.SUBMITTED
            || state === HANDOFF_STATE.STOPPED;
    }

    function cancelTimer() {
        if (timer !== null) {
            clearTimeoutFn(timer);
            timer = null;
        }
    }

    function notice(reason, detail = {}) {
        try {
            onNotice({ reason, detail, state, attempts });
        } catch (error) {
            // A logging sink must never be able to break a handoff.
        }
    }

    /**
     * Consume the sale the server named. Latches synchronously: the first caller in a
     * tick wins and every later one is told it lost.
     */
    function claim(result) {
        if (isFinished()) {
            notice(HANDOFF_NOTICE.LATE_RESULT_IGNORED, { result });

            return false;
        }

        const handoffUrl = result && result.handoff_url;

        if (!handoffUrl) {
            // Sold, but the server gave us nothing to submit. Keep waiting rather than
            // inventing a destination.
            notice(HANDOFF_NOTICE.SOLD_WITHOUT_ACTION, { result });

            return false;
        }

        state = HANDOFF_STATE.CLAIMING;
        cancelTimer();

        submitHandoff({
            handoffUrl,
            marketplaceSaleId: (result && result.marketplace_sale_id) ?? null,
        });

        state = HANDOFF_STATE.SUBMITTED;

        return true;
    }

    function claimPendingResolved() {
        const result = pendingResolvedResult;
        pendingResolvedResult = null;

        return result ? claim(result) : false;
    }

    function handleResult(result) {
        if (isFinished()) {
            notice(HANDOFF_NOTICE.LATE_RESULT_IGNORED, { result });

            return false;
        }

        if (result && result.status === 'sold') {
            return claim(result);
        }

        // The server has settled the cross-sell decision and there is nothing to hand
        // off: the rules engine declined, or the decision hold expired. Stop now instead
        // of burning the rest of the attempt limit — one reload renders whatever the
        // applicant should actually see next (the paystub ask, or the pending page).
        // Deliberately checked AFTER `sold`, so a sale arriving in the same response
        // still wins and is never reloaded away.
        if (result && result.status === 'resolved') {
            notice(HANDOFF_NOTICE.DECISION_RESOLVED, { result });
            state = HANDOFF_STATE.STOPPED;
            cancelTimer();
            reload();

            return false;
        }

        return false;
    }

    function scheduleNextPoll() {
        cancelTimer();

        timer = setTimeoutFn(() => {
            timer = null;
            runPoll();
        }, pollIntervalMs);
    }

    function afterPollSettled() {
        if (isFinished()) {
            return;
        }

        // An Echo event landed mid-request: answer it now rather than after the interval.
        if (recheckWhenIdle) {
            recheckWhenIdle = false;
            runPoll();

            return;
        }

        if (attempts >= maxAttempts) {
            // Only reached when the result above was NOT a sale - a sold result has
            // already latched into CLAIMING/SUBMITTED and short-circuited this.
            notice(HANDOFF_NOTICE.ATTEMPTS_EXHAUSTED);
            state = HANDOFF_STATE.STOPPED;
            cancelTimer();
            reload();

            return;
        }

        scheduleNextPoll();
    }

    function runPoll() {
        if (isFinished() || requestInFlight) {
            // One request at a time. A caller arriving now is remembered, not queued.
            if (!isFinished() && requestInFlight) {
                recheckWhenIdle = true;
            }

            return;
        }

        state = HANDOFF_STATE.POLLING;
        attempts += 1;
        requestInFlight = true;

        Promise.resolve()
            .then(() => checkStatus())
            .then(result => {
                handleResult(result);
            })
            .catch(error => {
                notice(HANDOFF_NOTICE.POLL_FAILED, { error });
            })
            .then(() => {
                requestInFlight = false;
                afterPollSettled();
            });
    }

    return {
        state: () => state,
        attempts: () => attempts,
        isSubmitted: () => state === HANDOFF_STATE.SUBMITTED,

        /** The page was rendered with the action already in hand: submit it shortly. */
        startWithResolvedHandoff(result) {
            if (isFinished()) {
                return;
            }

            state = HANDOFF_STATE.POLLING;
            cancelTimer();
            pendingResolvedResult = { status: 'sold', ...result };

            timer = setTimeoutFn(() => {
                timer = null;
                claimPendingResolved();
            }, resolvedHandoffDelayMs);
        },

        /** No sale yet: wait for one. */
        startPolling() {
            if (isFinished()) {
                return;
            }

            scheduleNextPoll();
        },

        /**
         * auth.js offers every MarketplaceSold broadcast here before falling back to its
         * global page reload. Returns true when this coordinator owns the event, which is
         * the caller's signal to preventDefault().
         *
         * Once mounted, this page owns every event it is offered - including ones about a
         * loan it is not showing. Returning false for those handed them straight back to
         * the global reload, which restarts this page's wait for no reason and, if the
         * form has already gone, tears down a POST/303 that is mid-flight to the buyer. A
         * borrower waiting on their own loan is not something another loan's news may
         * interrupt. Pages with no coordinator mounted still get the reload, which is
         * where that behavior belongs.
         *
         * Owning it never means claiming blindly: a matching event only triggers an
         * immediate status check, and the sale that gets consumed is the one the SERVER
         * names.
         */
        handleSoldEvent(detail = {}) {
            const eventLoanId = detail.loanId;

            if (eventLoanId !== null && eventLoanId !== undefined
                && Number(eventLoanId) !== Number(loanId)) {
                notice(HANDOFF_NOTICE.OTHER_LOAN_IGNORED, { eventLoanId, loanId });

                return true;
            }

            if (isFinished()) {
                // Still ours - reloading over a submitted handoff is exactly what this
                // exists to prevent - but there is nothing left to do.
                return true;
            }

            // The action is already in hand and only waiting out its display delay:
            // submit it now rather than spending a round trip re-asking.
            if (pendingResolvedResult) {
                cancelTimer();
                claimPendingResolved();

                return true;
            }

            if (requestInFlight) {
                recheckWhenIdle = true;

                return true;
            }

            cancelTimer();
            runPoll();

            return true;
        },

        /**
         * The event named a sale, the server named a different one. The server wins; this
         * only exists so the disagreement is visible instead of silent.
         */
        noteStaleSale(eventSaleId, serverSaleId) {
            if (eventSaleId !== null && eventSaleId !== undefined
                && Number(eventSaleId) !== Number(serverSaleId)) {
                notice(HANDOFF_NOTICE.STALE_SALE_IGNORED, { eventSaleId, serverSaleId });
            }
        },

        /** Component destruction, or anything else that ends this page's ownership. */
        stop() {
            if (state === HANDOFF_STATE.SUBMITTED) {
                // A submitted handoff is navigating; tearing down must not pretend it did
                // not happen, so the terminal state is preserved.
                cancelTimer();

                return;
            }

            state = HANDOFF_STATE.STOPPED;
            recheckWhenIdle = false;
            pendingResolvedResult = null;
            cancelTimer();
        },
    };
}
